import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import axios from "axios";
import { PageHero } from "@/components/sections/PageHero";
import { CTABand } from "@/components/sections/CTABand";
import { Lightbox } from "@/components/Lightbox";
import { SEO, imageGallerySchema } from "@/components/SEO";
import { IMAGES } from "@/lib/site";
import { ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BE = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (u?.startsWith("http") ? u : `${BE}${u}`);
const SPANS = ["lg:col-span-8", "lg:col-span-4", "lg:col-span-4", "lg:col-span-4", "lg:col-span-4", "lg:col-span-6", "lg:col-span-6", "lg:col-span-12"];

export default function AlbumDetail() {
  const { slug } = useParams();
  const [album, setAlbum] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/albums`).catch(() => ({ data: [] })),
      axios.get(`${API}/gallery?album=${encodeURIComponent(slug)}`).catch(() => ({ data: [] })),
    ]).then(([al, gi]) => {
      const found = (al.data || []).find(a => a.slug === slug);
      if (!found) setNotFound(true);
      else { setAlbum(found); setItems(gi.data || []); }
    }).finally(() => setLoading(false));
  }, [slug]);

  if (notFound) return <Navigate to="/gallery" replace />;

  return (
    <div data-testid={`album-page-${slug}`}>
      {album && (
        <SEO
          title={`${album.title} Gallery`}
          description={album.description || `Real work from the American Armor Coatings shop floor — ${album.title}. Powder coating, media blasting, and finishing in Hendersonville, TN.`}
          image={album.cover_image_url ? resolveUrl(album.cover_image_url) : IMAGES.powder1}
          path={`/gallery/${album.slug}`}
          jsonLd={imageGallerySchema(album, items, BE)}
        />
      )}
      <PageHero
        eyebrow="Gallery · Album"
        title={<>{album?.title || "Loading..."}<br /><span className="text-[#FF5722]">Album.</span></>}
        subtitle={album?.description || `A curated set of ${items.length} photo${items.length === 1 ? "" : "s"} from the American Armor Coatings production floor.`}
        image={album?.cover_image_url ? resolveUrl(album.cover_image_url) : IMAGES.powder1}
      />

      <section className="section px-6 lg:px-10">
        <div className="mb-10 flex items-center justify-between flex-wrap gap-4">
          <Link to="/gallery" className="btn-ghost" data-testid="album-back">
            <ArrowLeft className="w-4 h-4" /> All Albums
          </Link>
          <div className="text-xs uppercase tracking-widest text-zinc-500">{items.length} photo{items.length === 1 ? "" : "s"}</div>
        </div>

        {album?.is_case_study && (album?.client_name || album?.testimonial) && (
          <div className="mb-16 border-l-2 border-[#FF5722] pl-6 max-w-3xl" data-testid="case-study-block">
            {album.client_name && <div className="eyebrow mb-2">Case Study · {album.client_name}</div>}
            {album.testimonial && (
              <blockquote className="font-display text-2xl md:text-3xl leading-tight tracking-tight text-white">
                "{album.testimonial}"
              </blockquote>
            )}
          </div>
        )}

        {loading ? (
          <div className="text-zinc-500">Loading...</div>
        ) : items.length === 0 ? (
          <div className="text-zinc-500 text-center py-24">No photos in this album yet.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4">
            {items.map((it, i) => (
              <button
                key={it.id}
                onClick={() => setLightboxIdx(i)}
                className={`group relative overflow-hidden border border-white/10 ${SPANS[i % SPANS.length]} min-h-[280px] text-left cursor-zoom-in`}
                data-testid={`album-photo-${i}`}
              >
                <img src={resolveUrl(it.image_url)} alt={it.title} className="w-full h-full object-cover absolute inset-0 opacity-60 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="relative p-6 h-full flex flex-col justify-end min-h-[280px]">
                  <div className="eyebrow mb-2">{it.tag}</div>
                  <div className="font-display text-2xl md:text-3xl tracking-tight text-white">{it.title}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      <Lightbox items={items} index={lightboxIdx} onClose={() => setLightboxIdx(null)} onIndex={setLightboxIdx} />

      <CTABand />
    </div>
  );
}
